import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronRight, Code2, History, MessageSquare, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RepositoryField, RepositoryLinks } from "../RepositoryFields";

const tempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function CodeSecurityReview({ canManage = false }) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/service-audit/code-security/projects");
            setProjects(data || []);
            if (selected) {
                const refreshed = (data || []).find((item) => item.project_id === selected.project_id);
                if (refreshed) setSelected(refreshed);
            }
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const groups = useMemo(() => {
        const grouped = new Map();
        for (const project of projects) {
            const category = project.project_snapshot?.development_type || "Sin categoría";
            if (!grouped.has(category)) grouped.set(category, []);
            grouped.get(category).push(project);
        }
        return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b, "es"));
    }, [projects]);

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-orange-500" /><h3 className="font-semibold text-slate-900">Revisión de código y seguridad</h3></div>
                <p className="mt-1 text-sm text-slate-500">Cada proyecto conserva un historial independiente de versiones revisadas. Una nueva corrección crea la siguiente versión v1, v2, v3…</p>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="max-h-[62vh] overflow-y-auto">
                    {loading ? <div className="py-20 text-center text-slate-400">Cargando revisiones…</div> : groups.length ? (
                        <div className="divide-y divide-slate-200">
                            {groups.map(([category, items]) => (
                                <section key={category}>
                                    <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-slate-50/95 px-4 py-2.5 backdrop-blur">
                                        <div className="flex items-center gap-2"><Code2 className="h-4 w-4 text-slate-500" /><h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{category}</h4></div>
                                        <span className="text-xs text-slate-400">{items.length}</span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {items.map((project) => <ProjectRow key={project.project_id} project={project} onOpen={() => setSelected(project)} />)}
                                    </div>
                                </section>
                            ))}
                        </div>
                    ) : <div className="flex min-h-[420px] flex-col items-center justify-center text-center text-slate-400"><ShieldCheck className="mb-3 h-9 w-9 text-slate-300" /><p>No hay proyectos activos.</p></div>}
                </div>
            </div>

            <VersionDialog project={selected} open={Boolean(selected)} onOpenChange={(value) => !value && setSelected(null)} canManage={canManage} onChanged={load} />
        </div>
    );
}

function ProjectRow({ project, onOpen }) {
    const snapshot = project.project_snapshot || {};
    const latest = project.latest_version;
    return (
        <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpen(); } }} className="grid cursor-pointer gap-3 px-4 py-4 hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold text-orange-600">{snapshot.deployment_code}</span>
                    {latest && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${latest.status === "Revisada" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{latest.version_label} · {latest.status}</span>}
                </div>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900">{snapshot.project_name}</p>
                <p className="truncate text-xs text-slate-500">{snapshot.client_name}</p>
                <div className="mt-2 flex gap-3 text-[11px] text-slate-400"><span className="flex items-center gap-1"><History className="h-3.5 w-3.5" />{project.versions_count || 0} versiones</span>{latest && <span>{(latest.checklist || []).filter((item) => item.completed).length}/{(latest.checklist || []).length} checklist</span>}</div>
            </div>
            <div onClick={(event) => event.stopPropagation()}>
                <RepositoryLinks repositoryUrl={project.repository_url} backupRepositoryUrl={project.backup_repository_url} currentLabel="Deploy" />
            </div>
            <ChevronRight className="hidden h-4 w-4 text-slate-300 md:block" />
        </div>
    );
}

function VersionDialog({ project, open, onOpenChange, canManage, onChanged }) {
    const [versions, setVersions] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [repositoryUrl, setRepositoryUrl] = useState("");
    const [backupRepositoryUrl, setBackupRepositoryUrl] = useState("");
    const [checklist, setChecklist] = useState([]);
    const [newChecklist, setNewChecklist] = useState("");
    const [newObservation, setNewObservation] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const loadVersions = async (preferredId = null) => {
        if (!project) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/service-audit/code-security/projects/${project.project_id}/versions`);
            const next = data || [];
            setVersions(next);
            const id = preferredId || (next.some((item) => item.review_id === selectedId) ? selectedId : next[0]?.review_id) || "";
            setSelectedId(id);
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!project || !open) return;
        setRepositoryUrl(project.repository_url || "");
        setBackupRepositoryUrl(project.backup_repository_url || "");
        setSelectedId("");
        loadVersions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.project_id, open]);

    const selectedVersion = versions.find((item) => item.review_id === selectedId) || null;
    const editable = Boolean(canManage && selectedVersion && selectedVersion.status !== "Revisada");
    const canCreateVersion = Boolean(canManage && (!versions.length || versions[0]?.status === "Revisada"));

    useEffect(() => {
        setChecklist((selectedVersion?.checklist || []).map((item) => ({ ...item })));
        setNewChecklist("");
        setNewObservation("");
    }, [selectedId, selectedVersion?.updated_at, selectedVersion?.checklist]);

    if (!project) return null;

    const saveRepository = async () => {
        if (!canManage) return;
        setSaving(true);
        try {
            await api.patch(`/service-audit/code-security/projects/${project.project_id}`, {
                repository_url: repositoryUrl.trim() || null,
                backup_repository_url: backupRepositoryUrl.trim() || null,
            });
            toast.success("Repositorios actualizados");
            await onChanged();
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const createVersion = async () => {
        if (!canCreateVersion) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/service-audit/code-security/projects/${project.project_id}/versions`);
            toast.success(`${data.version_label} creada`);
            await loadVersions(data.review_id);
            await onChanged();
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const addChecklist = (event) => {
        event.preventDefault();
        if (!editable) return;
        const text = newChecklist.trim();
        if (!text) return;
        setChecklist((current) => [...current, { item_id: tempId(), text, completed: false }]);
        setNewChecklist("");
    };

    const saveVersion = async ({ markReviewed = false } = {}) => {
        if (!editable || !selectedVersion) return;
        setSaving(true);
        try {
            const payload = {
                checklist: checklist.map((item) => ({
                    item_id: item.item_id?.startsWith("tmp_") ? null : item.item_id,
                    text: item.text,
                    completed: Boolean(item.completed),
                })),
            };
            if (markReviewed) payload.status = "Revisada";
            await api.patch(`/service-audit/code-security/versions/${selectedVersion.review_id}`, payload);
            if (newObservation.trim()) {
                await api.post(`/service-audit/code-security/versions/${selectedVersion.review_id}/observations`, { text: newObservation.trim() });
            }
            toast.success(markReviewed ? `${selectedVersion.version_label} marcada como revisada` : "Versión guardada");
            await loadVersions(selectedVersion.review_id);
            await onChanged();
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[94vh] max-w-5xl flex-col overflow-hidden">
                <DialogHeader><DialogTitle>{project.project_snapshot?.project_name}</DialogTitle><p className="text-sm text-slate-500">{project.project_snapshot?.deployment_code} · {project.project_snapshot?.client_name}</p></DialogHeader>
                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                    <section className="mb-4 rounded-lg border border-slate-200 p-4">
                        <div className="grid gap-3 md:grid-cols-2">
                            <RepositoryField label="Repositorio GitHub en deploy" value={repositoryUrl} onChange={setRepositoryUrl} disabled={!canManage}/>
                            <RepositoryField label="Repositorio GitHub de backup" value={backupRepositoryUrl} onChange={setBackupRepositoryUrl} disabled={!canManage}/>
                        </div>
                        {canManage&&<div className="mt-3 flex justify-end"><Button variant="outline" onClick={saveRepository} disabled={saving}><Save className="mr-2 h-4 w-4" />Guardar repositorios</Button></div>}
                    </section>

                    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <aside className="rounded-lg border border-slate-200 p-3">
                            <div className="mb-3 flex items-center justify-between"><h4 className="text-sm font-semibold">Versiones</h4>{canCreateVersion && <Button size="sm" onClick={createVersion} disabled={saving}><Plus className="mr-1 h-3.5 w-3.5" />Nueva versión</Button>}</div>
                            <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
                                {versions.map((version) => <button type="button" key={version.review_id} onClick={() => setSelectedId(version.review_id)} className={`w-full rounded-md border p-3 text-left ${selectedId === version.review_id ? "border-orange-300 bg-orange-50" : "border-slate-200 hover:bg-slate-50"}`}><div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{version.version_label}</span>{version.status === "Revisada" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}</div><p className="mt-1 text-xs text-slate-500">{version.status}</p><p className="mt-1 text-[11px] text-slate-400">{version.created_at ? new Date(version.created_at).toLocaleDateString("es-EC") : ""}</p></button>)}
                                {!loading && versions.length === 0 && <p className="py-6 text-center text-xs text-slate-400">Sin versiones. Crea v1 para comenzar.</p>}
                            </div>
                        </aside>

                        <main className="min-w-0 space-y-4">
                            {selectedVersion ? <>
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-4"><div><p className="font-semibold text-slate-900">{selectedVersion.version_label}</p><p className="text-xs text-slate-500">Creada por {selectedVersion.created_by_name || "Usuario"}{selectedVersion.created_at ? ` · ${new Date(selectedVersion.created_at).toLocaleString("es-EC")}` : ""}</p></div><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${selectedVersion.status === "Revisada" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{selectedVersion.status}</span></div>

                                <section className="space-y-3 rounded-lg border border-slate-200 p-4">
                                    <h4 className="text-sm font-semibold text-slate-900">Checklist de cambios y correcciones</h4>
                                    <div className="max-h-56 space-y-2 overflow-y-auto pr-1">{checklist.map((item) => <div key={item.item_id} className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2"><Checkbox disabled={!editable} checked={Boolean(item.completed)} onCheckedChange={() => editable && setChecklist((current) => current.map((entry) => entry.item_id === item.item_id ? { ...entry, completed: !entry.completed } : entry))} aria-label={`${item.completed ? "Marcar pendiente" : "Marcar completado"}: ${item.text}`} className="mt-0.5" /><span className={`min-w-0 flex-1 text-sm ${item.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>{item.text}</span>{editable && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setChecklist((current) => current.filter((entry) => entry.item_id !== item.item_id))} aria-label={`Eliminar: ${item.text}`}><Trash2 className="h-3.5 w-3.5 text-slate-400" /></Button>}</div>)}{checklist.length === 0 && <p className="text-sm text-slate-400">Sin cambios registrados.</p>}</div>
                                    {editable && <form onSubmit={addChecklist} className="flex gap-2"><Label htmlFor="code-security-checklist-new" className="sr-only">Agregar cambio o corrección</Label><Input id="code-security-checklist-new" value={newChecklist} onChange={(event) => setNewChecklist(event.target.value)} placeholder="Agregar cambio o corrección…" /><Button type="submit" size="icon" disabled={!newChecklist.trim()} aria-label="Agregar cambio o corrección"><Plus className="h-4 w-4" /></Button></form>}
                                </section>

                                <section className="space-y-3 rounded-lg border border-slate-200 p-4">
                                    <h4 className="text-sm font-semibold text-slate-900">Observaciones</h4>
                                    <div className="max-h-48 space-y-3 overflow-y-auto pr-1">{(selectedVersion.observations || []).map((observation) => <div key={observation.observation_id} className="border-l-2 border-orange-300 pl-3"><p className="whitespace-pre-wrap text-sm text-slate-700">{observation.text}</p><p className="mt-1 text-xs text-slate-400">{observation.created_by_name || "Usuario"} · {new Date(observation.created_at).toLocaleString("es-EC")}</p></div>)}{(selectedVersion.observations || []).length === 0 && <p className="text-sm text-slate-400">Sin observaciones.</p>}</div>
                                    {editable && <Textarea rows={3} value={newObservation} onChange={(event) => setNewObservation(event.target.value)} placeholder="Agregar una observación para esta versión…" />}
                                </section>

                                {editable && <div className="flex flex-wrap justify-end gap-2"><Button variant="outline" onClick={() => saveVersion()} disabled={saving}>Guardar borrador</Button><Button onClick={() => saveVersion({ markReviewed: true })} disabled={saving}><CheckCircle2 className="mr-2 h-4 w-4" />Marcar versión revisada</Button></div>}
                            </> : <div className="flex min-h-[360px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 text-center text-slate-400"><History className="mb-2 h-8 w-8" /><p>Selecciona o crea una versión.</p></div>}
                        </main>
                    </div>
                </div>
                <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
